/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */

import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Input.module.scss';
import { NUMBERS_MAX_VALUE } from 'Config';
import NumberFormat from 'react-number-format';

export type InputPreset = 'default' | 'login' | 'duration';

export type Props = {
    preset?: InputPreset;
    format?: 'default' | 'money' | 'percentage' | 'number' | 'integer' | undefined;
    placeholder?: string;
    onValueChange?: (value: string | number | undefined) => void;
    noFixedDecimalCase?:boolean;
    numberDecimalCases?:number;
} & React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const Input = React.forwardRef<HTMLInputElement, Props>(({ preset, format, placeholder, noFixedDecimalCase, numberDecimalCases, onValueChange, onChange, ...props }, ref) => {
    const presetClassName = preset ? styles[preset] : styles.default;

    const { t } = useTranslation();
    const [textLength, setTextLength] = useState<number>(0);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.currentTarget.value;
        if (preset === 'duration') {
            // Remove all characters except digits and ':'
            value = value.replace(/[^\d:]/g, '');

            // Split hours and minutes
            const parts = value.split(':');

            const hh = parts[0]; // Allow unlimited digits for hours
            let mm = parts[1] ? parts[1].slice(0, 2) : ''; // Only two digits for minutes

            // Validate minutes if exactly two digits are entered
            if (mm.length === 2) {
                const mmNum = Number(mm);
                if (mmNum > 59) mm = '59';
                if (mmNum < 0) mm = '00';
            }

            // Format the result
            if (value.includes(':') || mm !== '') {
                value = `${hh}:${mm}`;
            } else {
                value = hh;
            }
        }



        setTextLength(value.length);
        if (onChange) onChange({ ...e, currentTarget: { ...e.currentTarget, value } });
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        let newValue = e.currentTarget.value;
        if (!newValue) return;

        if (preset === 'duration') {
            let [hh, mm] = newValue.split(':');

            // Se só houver horas
            if (!mm) {
                // Se digitou 1 número, adiciona 0 antes
                if (hh.length === 1) hh = '0' + hh;

                // Completa com minutos
                mm = '00';
                newValue = `${hh}:${mm}`;
            } else {
                // Se houver minutos, garante dois dígitos
                if (hh.length === 1) hh = hh + '0';
                if (mm.length === 1) mm = mm + '0';
                newValue = `${hh}:${mm}`;
            }

            if (onChange) {
                onChange({ ...e, currentTarget: { ...e.currentTarget, value: newValue } });
            }
        }

        // Se houver onBlur externo, também dispara
        if (props.onBlur) {
            props.onBlur(e)
        } else {
            e.target.value = e.target.value.trim();
        }
    };

    const withValueCap = (inputObj: any) => {
        const { value } = inputObj;
        if (value <= NUMBERS_MAX_VALUE) return inputObj;
    };

    return (
        <>
            {(!format && <input
                ref={ref}
                type={props.type || 'text'}
                {...props}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`${styles.input} ${presetClassName} ${props.className || ''} ${props.disabled ? styles.disabled : ''} `}
                placeholder={placeholder}
            />)}

            {(format === 'money' || format === 'percentage' || format === 'number' || format === 'integer') && <NumberFormat
                getInputRef={ref}
                displayType="input"
                thousandSeparator={format === 'money' || format === 'percentage' || format === 'number' ? ' ' : ''}
                value={props.value as number}
                type={'text'}
                decimalScale={numberDecimalCases ? numberDecimalCases : format === 'money' || format === 'percentage' || format === 'number' ? 2 : 0}
                placeholder={placeholder}
                fixedDecimalScale={(format === 'money' || format === 'percentage' || format === 'number') && !noFixedDecimalCase ? true : false}
                decimalSeparator={format === 'money' || format === 'percentage' || format === 'number' ? ',' : false}
                onValueChange={e => {
                    if (onValueChange) {
                        onValueChange(e.floatValue ?? (props.type === 'number' || format === 'integer' ? undefined : ''));
                    }
                }}
                onChange={e => {
                    setTextLength(e.currentTarget.value.length);
                    if (onChange) {
                        onChange(e);
                    }
                }}
                className={`${styles.input} ${styles.inputMoney} ${props.className || ''} ${props.disabled ? styles.disabled : ''}
                            `}
                disabled={props.disabled}
                isAllowed={withValueCap}
            />}

            {textLength === props.maxLength && <div className={styles.maxLength}>{t('common.field_max_length', { value: textLength })}</div>}
        </>
    );
});

export default memo(Input);
